// "Moved 3×" in task lists (Carla, 2026-10-21): a task whose due date has moved three times or more
// says so. A list provides the counts once (one request for its tasks, GET /api/tasks/move-counts,
// which answers only for tasks this person may see) and each row reads its own.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchMoveCounts, movedTag, seriesIdOf } from '../../utils/taskDueChanges';
import './overdue.css';

const MoveCounts = createContext(null);

export function useMoveCounts(tasks) {
  const ids = useMemo(() => [...new Set((tasks || []).filter((t) => t && !t.isBirthday).map(seriesIdOf).filter((i) => /^\d+$/.test(i)))].sort(), [tasks]);
  const key = ids.join(',');
  const [counts, setCounts] = useState({});
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const again = () => setTick((n) => n + 1);
    window.addEventListener('aidey:tasksUpdated', again);
    return () => window.removeEventListener('aidey:tasksUpdated', again);
  }, []);
  useEffect(() => {
    let live = true;
    if (!key) { setCounts({}); return undefined; }
    // A tag that could not be read is simply not shown: it is a hint, never the only place it is said
    // (the task's History section says it in full).
    fetchMoveCounts(key.split(',')).then((c) => { if (live) setCounts(c); }).catch(() => {});
    return () => { live = false; };
  }, [key, tick]);
  return counts;
}

export function MoveCountsProvider({ tasks, children }) {
  const counts = useMoveCounts(tasks);
  return <MoveCounts.Provider value={counts}>{children}</MoveCounts.Provider>;
}

export function MovedTag({ task, moves }) {
  const counts = useContext(MoveCounts);
  const n = moves ?? (counts ? counts[seriesIdOf(task)] : 0);
  const text = movedTag(n);
  if (!text) return null;
  return <span className="ov-tag" data-testid="moved-tag" title={`Its due date has moved ${n} times`}>{text}</span>;
}
